import {
  EvidenceTypeEnum,
  EvidenceUploadMode,
  getApiQualityExternalLoadsV1ExternalLoadsEvidenceListQueryKey,
  getApiQualitySessionsV2TradeCompanySessionEvidenceListQueryKey,
  PaginatedEvidenceList,
  useApiQualityExternalLoadsV1ExternalLoadsEvidenceList,
  useApiQualitySessionsV2TradeCompanySessionEvidenceList,
  useQueryClient,
} from 'api';
import { logger } from 'monitor';
import { useCallback } from 'react';

const DEFAULT_EVIDENCE_SORT_ORDER = '-created_at' as const;
const defaultOptions = {
  order: DEFAULT_EVIDENCE_SORT_ORDER,
  pageSize: 25,
};

export const useGetEvidenceList = ({
  mode,
  parentId,
  options = defaultOptions,
  enabled = true,
}: {
  mode?: EvidenceUploadMode;
  parentId: string | number;
  options?: {
    page_size?: number;
    type?: EvidenceTypeEnum;
    page?: number;
    order?: 'created_at' | '-created_at' | 'id' | '-id' | 'type' | '-type';
    include_ids?: number[];
  };
  enabled?: boolean;
}) => {
  const hasParent = Boolean(parentId);
  const hasUploadMode = Boolean(mode);

  const queryClient = useQueryClient();

  const {
    data: tradeCompanySessionEvidence,
    isLoading: isTradeCompanySessionLoading,
    isFetched: isTradeCompanySessionFetched,
  } = useApiQualitySessionsV2TradeCompanySessionEvidenceList(Number(parentId), options, {
    query: {
      enabled: enabled && mode === 'trade-company-session' && hasParent && hasUploadMode,
    },
  });

  const {
    data: externalLoadEvidence,
    isLoading: isExternalLoadLoading,
    isFetched: isExternalLoadFetched,
  } = useApiQualityExternalLoadsV1ExternalLoadsEvidenceList(Number(parentId), options, {
    query: {
      enabled: enabled && mode === 'external-load' && hasParent && hasUploadMode,
    },
  });

  const modeConfig: Record<
    string,
    { data: PaginatedEvidenceList | undefined; isLoading: boolean; isFetched: boolean }
  > = {
    'external-load': {
      data: externalLoadEvidence,
      isFetched: isExternalLoadFetched,
      isLoading: isExternalLoadLoading,
    },
    'trade-company-session': {
      data: tradeCompanySessionEvidence,
      isFetched: isTradeCompanySessionFetched,
      isLoading: isTradeCompanySessionLoading,
    },
  };
  const data = mode ? modeConfig[mode]?.data : undefined;
  const isLoading = mode ? modeConfig[mode]?.isLoading : false;
  const isFetched = mode ? modeConfig[mode]?.isFetched : false;

  const refetchEvidence = useCallback(
    (parentIdOverride?: number | string, refetchAllEvidence?: boolean) => {
      if (!hasUploadMode) {
        logger.warn('No upload mode specified for refetching evidence');
        return;
      }
      const refetchOptions = refetchAllEvidence ? undefined : options;
      const effectiveParentId = parentIdOverride ?? parentId;
      logger.info(`Refetching evidence for mode ${mode} and parentId ${effectiveParentId} with options`, {
        refetchOptions,
      });

      switch (mode) {
        case 'trade-company-session':
          queryClient.invalidateQueries({
            queryKey: getApiQualitySessionsV2TradeCompanySessionEvidenceListQueryKey(
              Number(effectiveParentId),
              refetchOptions
            ),
          });
          break;

        case 'external-load':
          queryClient.invalidateQueries({
            queryKey: getApiQualityExternalLoadsV1ExternalLoadsEvidenceListQueryKey(
              Number(effectiveParentId),
              refetchOptions
            ),
          });
          break;
      }
    },
    [queryClient, hasUploadMode, mode, parentId, options]
  );

  return {
    count: data?.count,
    data: data?.results,
    isFetched,
    isLoading,
    next: data?.next,
    refetchEvidence,
  };
};
